import { styled } from '@linaria/react'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { colors } from '../styles/colors'
import { Typography } from '../ui/Typography'
import TonIcon from '../svgr/TonIcon'
import { Rhytm } from '../ui/Rhytm'
import { useUserTasks } from '../hooks/useBonusData'
import {
  formatTaskProgress,
  getTaskStatus,
  type UserTaskStatus,
} from '../api/bonusApi'
import { formatTon } from '../utils/formatTon'
import { useAppContext } from '../contexts/App.context'
import { useTelegram } from '../hooks/useTelegram'
import { useNavigate } from 'react-router-dom'
import { Dialog } from '../dialogs/Dialog'
import { TaskCompletedDialog } from '../dialogs/TaskCompletedDialog'
import { trackTaskCompleted } from '../utils/amplitude'

interface TaskConfig {
  title: string
  message: string
  buttonText: string
  buttonId: string
  navigationPath: string
}

export const BonusTasksTab = () => {
  const { language } = useAppContext()
  const { data, isLoading, error } = useUserTasks()
  const { formatMessage } = useIntl()
  const { tg, isTelegramVersionSupported } = useTelegram()
  const navigate = useNavigate()
  const [showCompletedDialog, setShowCompletedDialog] = useState(false)

  // Create task configs with formatMessage to ensure keys are extracted
  const taskConfigs: Record<string, TaskConfig> = {
    first_vote: {
      title: formatMessage({
        id: 'bonus.task.firstVote.title',
        defaultMessage: 'Сделайте первый голос',
      }),
      message: formatMessage({
        id: 'bonus.task.firstVote.message',
        defaultMessage:
          'Перейдите в раздел боксов и проголосуйте в любом боксе, чтобы завершить это задание.',
      }),
      buttonText: formatMessage({
        id: 'bonus.task.firstVote.button',
        defaultMessage: 'К боксам',
      }),
      buttonId: 'go_to_main',
      navigationPath: '/',
    },
    create_first_box: {
      title: formatMessage({
        id: 'bonus.task.createFirstBox.title',
        defaultMessage: 'Создайте свой первый бокс',
      }),
      message: formatMessage({
        id: 'bonus.task.createFirstBox.message',
        defaultMessage:
          'Создайте бокс для голосования, чтобы получить награды.',
      }),
      buttonText: formatMessage({
        id: 'bonus.task.createFirstBox.button',
        defaultMessage: 'Создать бокс',
      }),
      buttonId: 'create-box',
      navigationPath: '/create-keynesian-box',
    },
    invite_5_friends: {
      title: formatMessage({
        id: 'bonus.task.inviteFriends.title',
        defaultMessage: 'Пригласите друзей',
      }),
      message: formatMessage({
        id: 'bonus.task.inviteFriends.message',
        defaultMessage:
          'Используйте реферальную ссылку на вкладке Рефералы, чтобы пригласить друзей и заработать бонусы.',
      }),
      buttonText: formatMessage({
        id: 'bonus.task.inviteFriends.button',
        defaultMessage: 'К рефералам',
      }),
      buttonId: 'to-referrals',
      navigationPath: '/bonuses/referrals',
    },
  }

  const showTaskPopup = (config: TaskConfig) => {
    if (isTelegramVersionSupported(6.1)) {
      tg.showPopup(
        {
          title: config.title,
          message: config.message,
          buttons: [
            {
              type: 'default',
              text: config.buttonText,
              id: config.buttonId,
            },
            { type: 'cancel' },
          ],
        },
        buttonId => {
          if (buttonId === config.buttonId) {
            navigate(config.navigationPath)
          }
        },
      )
    } else {
      const confirmed = confirm(
        `${config.title}: ${config.message}\n\nClick OK to ${config.buttonText}`,
      )
      if (confirmed) {
        navigate(config.navigationPath)
      }
    }
  }

  const handleTaskClick = async (taskKey: string, status: UserTaskStatus) => {
    if (isTelegramVersionSupported(6.1)) {
      tg.HapticFeedback.impactOccurred('light')
    }

    if (status === 'completed') {
      trackTaskCompleted(taskKey)
      setShowCompletedDialog(true)
      return
    }

    const taskConfig = taskConfigs[taskKey]

    if (taskConfig) {
      showTaskPopup(taskConfig)
    } else {
      if (isTelegramVersionSupported(6.1)) {
        tg.HapticFeedback.notificationOccurred('error')
        tg.showPopup({
          title: formatMessage({
            id: 'bonus.task.inProgress.title',
            defaultMessage: 'Действие выполняется',
          }),
          message: formatMessage({
            id: 'bonus.task.inProgress.message',
            defaultMessage: 'Пожалуйста, попробуйте начать немного позже',
          }),
          buttons: [{ type: 'ok' }],
        })
      } else {
        alert(
          `${formatMessage({
            id: 'bonus.task.inProgress.title',
            defaultMessage: 'Действие выполняется',
          })}: ${formatMessage({
            id: 'bonus.task.inProgress.message',
            defaultMessage: 'Пожалуйста, попробуйте начать немного позже',
          })}`,
        )
      }
    }
  }

  if (isLoading) {
    return (
      <CenteredContainer>
        <Typography fontSize={14} color={colors.textSecondary}>
          <FormattedMessage id="bonus.loading" defaultMessage="Загрузка..." />
        </Typography>
      </CenteredContainer>
    )
  }

  if (error) {
    return (
      <CenteredContainer>
        <Typography fontSize={14} color={colors.textSecondary}>
          <FormattedMessage
            id="bonus.error"
            defaultMessage="Ошибка загрузки заданий"
          />
        </Typography>
      </CenteredContainer>
    )
  }

  if (!data || data.tasks.length === 0) {
    return (
      <CenteredContainer>
        <Typography fontSize={14} color={colors.textSecondary}>
          <FormattedMessage
            id="bonus.noTasks"
            defaultMessage="Нет доступных заданий"
          />
        </Typography>
      </CenteredContainer>
    )
  }

  const taskColors = {
    completed: colors.success,
    in_progress: colors.textMuted,
    pending: colors.textSecondary,
  }

  return (
    <>
      <TasksContainer>
        {data.tasks.map(task => {
          const title = language === 'en' ? task.titleEn : task.titleRu
          const description =
            language === 'en' ? task.descriptionEn : task.descriptionRu

          const status = getTaskStatus(task)

          const progress = formatTaskProgress(
            task,
            formatMessage({
              id: 'common.of',
              defaultMessage: 'из',
            }),
          )

          return (
            <TaskCard
              key={task.taskKey}
              status={status}
              onClick={async () => await handleTaskClick(task.taskKey, status)}
              clickable={status !== 'completed'}
            >
              <TaskInfo>
                <Typography fontWeight={700} fontSize={14}>
                  {title}
                </Typography>

                <Typography
                  fontSize={10}
                  color={taskColors[status] ?? colors.textSecondary}
                >
                  {description}
                  {progress && ` (${progress})`}
                </Typography>
              </TaskInfo>

              <Rhytm gridFlowDirection="column" alignItems="center" gap="2px">
                <Typography fontSize={14} fontWeight={600} font="roboto">
                  {formatTon(task.reward, 2)}
                </Typography>
                <TonIcon />
                {status === 'completed' && (
                  <CompletedCheckmark fontSize={10} fontWeight={700}>
                    ✓
                  </CompletedCheckmark>
                )}
              </Rhytm>
            </TaskCard>
          )
        })}
      </TasksContainer>

      {showCompletedDialog && (
        <Dialog onClose={() => setShowCompletedDialog(false)}>
          {({ onClose }) => <TaskCompletedDialog onClose={onClose} />}
        </Dialog>
      )}
    </>
  )
}

// ============= Styles =============

const CenteredContainer = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;
  padding: 16px 0;
  text-align: center;
`

const TasksContainer = styled.div`
  display: grid;
  gap: 4px;
`

const TaskCard = styled.div<{ status: string; clickable?: boolean }>`
  border: 1px solid ${colors.borderPrimary};
  border-radius: 24px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props =>
    props.status === 'completed'
      ? colors.surfaceSecondary
      : colors.surfacePrimary};
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease;

  &:hover {
    background: ${props =>
      props.clickable ? colors.surfaceSecondary : 'inherit'};
    transform: ${props => (props.clickable ? 'translateY(-1px)' : 'none')};
  }

  &:active {
    transform: ${props => (props.clickable ? 'translateY(0)' : 'none')};
  }
`

const TaskInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`

const CompletedCheckmark = styled(Typography)`
  background: ${colors.success};
  color: ${colors.white};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`
